import { useMemo } from 'react';
import { useGetGeoClustersQuery } from '../api/studentNetworkMapsApi';
import { type GeoJsonFeature, type ClusterData } from '../api/studentNetworkV2Api.types';
import { type useMapViewport } from './useMapViewport';

// This controls the PostGIS clustering radius (ground_resolution * marker_width).
// Must match the v1 value of 12 for equivalent cluster distribution.
// Note: the visual markerWidth for rendering (in StudentCluster.tsx) is 20.
const MARKER_WIDTH = 12;

export function useStudentClusters(viewport: ReturnType<typeof useMapViewport>) {
    const queryResult = useGetGeoClustersQuery(
        {
            filters: {
                viewports: viewport?.viewports,
                zoom: viewport?.zoom ?? 3,
                marker_width: MARKER_WIDTH,
            },
        },
        { skip: !viewport },
    );

    const { data, isFetching, error, refetch } = queryResult;

    const clusters = useMemo(() => {
        if (!data?.features) return [];
        return data.features.map(featureToCluster).filter(c => Number.isFinite(c.lat) && Number.isFinite(c.lng));
    }, [data]);

    return { clusters, isFetching, error, retry: refetch };
}

function featureToCluster(feature: GeoJsonFeature): ClusterData {
    return {
        id: feature.id,
        lat: feature.geometry.coordinates[1],
        lng: feature.geometry.coordinates[0],
        count: feature.properties.count,
        clusterHull: feature.properties.clusterHull,
    };
}
